import { getHistory, FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";

import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { IEHireTrackerReportModel, IEHireTrackerReportRequestDTO, IEHireTrackerReportResponseDTO } from "./IEHireTrackerReportModel";
import moment from "moment";
import { AppUrls } from "AppUrls";
//import { IEReportIEDTO, IEReportResponseDTO, IEReportVariationGridDTO } from "../Shared/IEReportModelBase";
import { csvAxiosRequestConfig, exportCSV } from "Utils/Utils";
import { HireTrackerItemModel } from "./HireTrackerItemModel";
import { HireTrackerItemViewModel } from "./HireTrackerItemViewModel";
import type { IObservableArray } from "mobx";
import { HireStatusTypeModel } from "Globals/Models/Domain/HireStatusTypeModel";

export class IEHireTrackerReportViewModel extends ViewModelBase<IEHireTrackerReportModel> {
    // #region Constructors and Disposers

    constructor() {
        super(new IEHireTrackerReportModel());

        this.setDecorators(IEHireTrackerReportViewModel);
    }

    //@observable public reportViewModel: IEHireTrackerReportResponseDTO | null = null;

    @observable public canExportCSV: boolean = false;

    @action
    public handleSetIsLoading(val: boolean) {
        this.setIsLoading(val);
    }

    @action
    public setCanExportCSV(val: boolean) {
        this.canExportCSV = val;
    }

    @computed
    public get getTopDateFormatted(): string {
        return this.model.reportEndDateTop ? moment(this.model.reportEndDateTop).format("DD/MM/YYYY").toString() : "Please select a date";
    }

    @observable
    public reportViewModel: IEHireTrackerReportResponseDTO | null = null;

    @observable
    public items: IObservableArray<HireTrackerItemViewModel> = observable([]);

    @observable
    public hireStatuses: IObservableArray<HireStatusTypeModel> = observable([]);

    @action
    public handleReturnStatus = (rowData: HireTrackerItemViewModel) => {
        return this.hireStatuses.find((s) => s.id === rowData.hireStatusId);
    };

    @action
    private createViewModels = (data: IEHireTrackerReportResponseDTO) => {
        this.items.replace([]);
        this.hireStatuses.replace([]);
        data.items.forEach((item) => {
            let model: HireTrackerItemModel = new HireTrackerItemModel();
            model.fromDto(item);
            let viewModel: HireTrackerItemViewModel = new HireTrackerItemViewModel(model);
            this.items.push(viewModel);
        });
        data.hireStatuses.forEach((item) => {
            this.hireStatuses.push(new HireStatusTypeModel(item));
        });
    };

    // #region Server Actions

    @action
    public runReport = async (ieId: string | null, projectId: string | null): Promise<ApiResult<IEHireTrackerReportResponseDTO>> => {
        this.setIsLoading(true);
        const toDate = this.model.reportEndDateTop;
        const request: IEHireTrackerReportRequestDTO = {
            projectId: projectId,
            ieId: ieId,
            toDate: toDate,
        };
        let apiResult = await this.Post<IEHireTrackerReportResponseDTO>(AppUrls.Server.Report.IE.GetIEHireTrackerReport, request);
        if (apiResult) {
            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.reportViewModel = apiResult.payload;
                    this.createViewModels(apiResult.payload);
                });
            } else {
                console.log(apiResult.errors);
            }
        }
        this.setIsLoading(false);
        return apiResult;
    };

    @action
    public generateIEHireTrackerReportCSV = async (ieId: string | null, projectId: string | null) => {
        this.setIsLoading(true);

        // JC: Download a CSV file using a HTTP POST request.
        // Source: https://stackoverflow.com/a/55138366

        const toDate = this.model.reportEndDateTop;
        const request: IEHireTrackerReportRequestDTO = {
            projectId: projectId,
            ieId: ieId,
            toDate: toDate,
        };

        await exportCSV(AppUrls.Server.Report.IE.GenerateIEHireTrackerReportCSV, request, await this.getConfig(true, csvAxiosRequestConfig)).finally(() =>
            this.setIsLoading(false),
        );
    };

    public server: ServerViewModel = new ServerViewModel();

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
    };

    @action
    public handleCancel = (): void => {
        getHistory().goBack();
    };

    // #endregion Server Actions

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<IEHireTrackerReportModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    // #region Snackbar

    @observable
    public snackbarState = false;

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @observable
    public snackType = "";

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";
    // #endregion

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boilerplate
}
